import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { ImplementationKitArticle } from "@common/implementation-kit/implementation-kit-article.enum";
import { IConfigGroup } from "@org-common/lib/configuration/configuration.interfaces";
import { ConfigurationAuthService } from "@org-common/lib/configuration/configuration-auth.service";
import { ConfigArea } from "@org-common/lib/configuration/configuration-types";

export const allOrganisationConfigGroups = (): IConfigGroup[] => [
    {
        key: "System",
        items: [
            {
                name: "Organisation profile",
                area: ConfigArea.OrganisationProfile,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
            },
            {
                name: "External links",
                area: ConfigArea.ExternalLink,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.OrganisationExternalDashboard,
                enableFeatureText: "external links",
            },
            {
                name: "Personal profile categories",
                area: ConfigArea.PersonalProfileCategories,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
            },
            {
                name: "Email customisation",
                area: ConfigArea.CustomWelcomeEmail,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
            },
            {
                name: "Calendar integration",
                area: ConfigArea.CalendarIntegration,
            },
            {
                name: "Developer API",
                area: ConfigArea.Api,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.OrganisationApi,
                enableFeatureText: "developer API",
            },
            {
                name: "Lumia Assistant",
                area: ConfigArea.AI,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.PlatformAI,
                enableFeatureText: "Lumia Assistant",
                supportArticleId: ImplementationKitArticle.AIAssistant,
                supportText: "Find out more about the Lumia Assistant on the Adapt support site.",
            },
            // {
            //     name: "Feature flags",
            //     area: ConfigArea.FeatureFlags,
            //     accessVerifier: ConfigurationAuthService.StakeholderManagerAccess,
            // },
        ],
    },
    {
        key: "Organisation",
        items: [
            {
                name: "Purpose & vision",
                area: ConfigArea.PurposeAndVision,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.PurposeAndVision,
                enableFeatureText: "purpose & vision",
                supportArticleId: ImplementationKitArticle.ValuePurposeVision,
                supportText: "Find out more about purpose and vision on the Adapt support site.",
            },
            {
                name: "Owners vision",
                area: ConfigArea.OwnersVision,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CompassVision,
                enableFeatureText: "owners vision",
                supportText: "An owner's vision represents the long-term aspirations and future state that the business owner(s) want to achieve",
            },
            {
                name: "Resilient business goals",
                area: ConfigArea.ResilientBusinessGoals,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CompassResilientBusinessGoals,
                enableFeatureText: "resilient business goals",
                supportArticleId: ImplementationKitArticle.ResilientBusinessGoals,
                supportText: "Find out more about resilient business goals on the Adapt support site.",
            },
            {
                name: "Objectives & key results",
                area: ConfigArea.ObjectivesAndKeyResults,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.StewardshipObjectives,
                enableFeatureText: "objectives & key results",
                supportArticleId: ImplementationKitArticle.IntroducingObjectivesAndKeyResults,
                supportText: "Find out more about objectives on the Adapt support site.",
            },
            {
                name: "Resilient business assessment",
                area: ConfigArea.ResilientBusinessAssessment,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CulturalNetworkResilientBusinessAssessment,
                enableFeatureText: "resilient business assessment",
                supportArticleId: ImplementationKitArticle.ResilientBusinessAssessment,
                supportText: "Find out more about resilient business assessment on the Adapt support site.",
            },
            {
                name: "Bullseye",
                area: ConfigArea.Bullseye,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.Bullseye,
                enableFeatureText: "bullseye",
                supportArticleId: ImplementationKitArticle.BullseyeOverview,
                supportText: "Find out more about bullseye on the Adapt support site.",
            },
            {
                name: "Strategic anchors",
                area: ConfigArea.StrategicAnchors,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.StrategicAnchors,
                supportArticleId: ImplementationKitArticle.StrategicAnchorsOverview,
                supportText: "Find out more about strategic anchors on the Adapt support site.",
            },
            {
                name: "Strategic goals",
                area: ConfigArea.StrategicGoals,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.StrategicGoals,
                supportArticleId: ImplementationKitArticle.StrategicGoalsOverviewEmbed,
                supportText: "Find out more about strategic goals on the Adapt support site.",
            },
            {
                name: "Strategic inputs",
                area: ConfigArea.StrategicInputs,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.StrategicInputs,
                supportArticleId: ImplementationKitArticle.InputsOverview,
                supportText: "Find out more about strategic inputs on the Adapt support site.",
            },
            {
                name: "Journey progress",
                area: ConfigArea.Progress,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.Progress,
                enableFeatureText: "journey progress",
                supportText: "The journey progress dashboard allows you to track your organisation's progress. This will be set up for your organisation by your coach.",
            },
            {
                name: "Groups (Teams)",
                area: ConfigArea.Team,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                supportArticleId: ImplementationKitArticle.UsingTheAdaptTeamModel,
                supportText: "Find out more about groups on the Adapt support site.",
            },
            {
                name: "Actions",
                area: ConfigArea.Kanban,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.StewardshipWorkKanban,
                enableFeatureText: "actions",
                supportArticleId: ImplementationKitArticle.UsingKanbanBoards,
                supportText: "Find out more about effective team stewardship on the Adapt support site.",
            },
            {
                name: "Meetings",
                area: ConfigArea.Meetings,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.StewardshipWorkMeetings,
                enableFeatureText: "meetings",
                supportArticleId: ImplementationKitArticle.UsingKanbanBoards,
                supportText: "Find out more about effective team stewardship on the Adapt support site.",
            },
            {
                name: "Labels",
                area: ConfigArea.Labels,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                supportArticleId: ImplementationKitArticle.LabelsEmbed,
                supportText: "Find out more about effective use of labels on the Adapt support site.",
            },
        ],
    },
    {
        key: "Economic engine",
        items: [
            {
                name: "Stakeholders",
                area: ConfigArea.Stakeholders,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.StakeholdersPeople,
                enableFeatureText: "stakeholders",
                supportText: "Find out more about stakeholders on the Adapt support site.",
            },
        ],
    },
    {
        key: "Leadership & culture",
        items: [
            {
                name: "Values",
                area: ConfigArea.ValuesConstitution,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CompassValuesConstitution,
                enableFeatureText: "values",
                supportArticleId: ImplementationKitArticle.ValuePurposeVision,
                supportText: "Find out more about values and value constitutions on the Adapt support site.",
            },
            {
                name: "Cultural Index",
                area: ConfigArea.CulturalIndex,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CulturalNetworkCulturalIndex,
                supportArticleId: ImplementationKitArticle.CulturalIndex,
                supportText: "Find out more about the cultural index on the Adapt support site.",
            },
            {
                name: "Career Valuation",
                area: ConfigArea.CareerValuation,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CulturalNetworkCareerValuation,
                supportArticleId: ImplementationKitArticle.CareerValuation,
                supportText: "Find out more about career valuations on the Adapt support site.",
            },
            {
                name: "Cultural Leadership Framework",
                area: ConfigArea.CulturalLeadership,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CulturalNetworkCulturalLeadershipCohort,
                supportArticleId: ImplementationKitArticle.BuildCulturalLeadership,
                supportText: "Find out more about cultural leadership on the Adapt support site.",
            },
            {
                name: "Peer Catch-up",
                area: ConfigArea.PeerCatchUp,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.PeoplePeerCatchUp,
                supportArticleId: ImplementationKitArticle.PeerCatchUp,
                supportText: "Find out more about Peer Catch-ups on the Adapt support site.",
            },
            {
                name: "Employee engagement",
                area: ConfigArea.EmployeeEngagement,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CulturalNetworkEmployeeEngagement,
                enableFeatureText: "employee engagement",
                supportArticleId: ImplementationKitArticle.EmployeeEngagementSurvey,
                supportText: "Find out more about employee engagement on the Adapt support site.",
            },
            {
                name: "Team assessment",
                area: ConfigArea.TeamAssessment,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.CulturalNetworkTeamAssessment,
                enableFeatureText: "team assessment",
                supportArticleId: ImplementationKitArticle.UsingTheAdaptTeamModel,
                supportText: "Find out more about team assessment on the Adapt support site.",
            },
        ],
    },
    {
        key: "Organisation design",
        items: [
            {
                name: "Organisation map",
                area: ConfigArea.Tier1,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                supportArticleId: ImplementationKitArticle.Architecture,
                supportText: "Find out more about the organisation map on the Adapt support site.",
            },
            {
                name: "Systems",
                area: ConfigArea.System,
                accessVerifier: ConfigurationAuthService.ConfigureOrganisation,
                featureName: FeatureName.ArchitectureSystem,
                enableFeatureText: "systems",
                supportArticleId: ImplementationKitArticle.SystemiseYourBusiness,
                supportText: "Find out more about Systems & processes (systems & guidance materials) on the Adapt support site.",
            },
        ],
    },
];
